<app-dashboard-shell
  title="OTR - Labor Tracking"
  [lastReload]="lastReload"
  [loading]="loading"
  [showRefresh]="true"
  (refresh)="triggerReload()"
  class="otr-labor-tracking-container"
>
  <div class="p-col-12">
    <div class="dashboard-card">
      <div class="dashboard-card-header p-mb-3 save-header">
        <div>
          <div class="title-row">
            <h4 class="p-mb-0">
              OTR Crew Pay<app-error-icon *ngIf="error" [error]="error"></app-error-icon>
            </h4>
            <app-dashboard-info-v2>
              <div class="p-mb-2">
                One row per crew member per OTR job whose last scheduled event ends in the
                selected period, grouped into Wednesday–Tuesday payment periods by that last-event
                date. Only booked and invoiced jobs are counted — the same stages as the
                OTR - System Revenue dashboard — so leads, estimates and cancelled jobs are
                excluded regardless of what is scheduled against them. Completion status is not
                a factor: a booked job that has not been marked complete still appears.
                Adjust checkboxes and amounts, then click
                Save — each changed job is updated and its crew pay recalculated (like the Job page).
              </div>
              <div class="p-mb-2">
                <strong>Pay Labor to FP</strong> means the Franchise Partner pays that crew member
                directly, so their crew pay is zeroed here and the amount is forwarded to the FP as
                a lump sum. Their phase checkboxes still show which phases they worked, but are
                locked and show no rate. On the OTR - System Revenue dashboard the same amount moves
                out of Flat Labor and into OTR, leaving the Total unchanged. Untick and Save to
                restore their pay.
              </div>
              <div class="p-mb-2">
                The three rightmost columns track document compliance. <strong>Contract For This
                Job</strong> is per job and saves with the Save button. <strong>ACH Info</strong>
                and <strong>W-9</strong> are per crew member (they apply to all of that person's
                jobs) and save immediately when toggled.
              </div>
              <div class="p-mb-2">
                <strong>Copy Data</strong> puts every payment period below on the clipboard,
                tab-separated so it pastes straight into spreadsheet columns; <strong>Copy</strong>
                on a period header does the same for that period alone. Use
                <strong>Download as CSV</strong> for a file instead. All three carry the rows
                exactly as shown, with each component column carrying the dollar rate it
                contributes — 0.00 for a member whose labor is paid to the FP, blank where they are
                not assigned to that phase. Copying and exporting are disabled while there are
                unsaved changes.
              </div>
              <div class="p-mb-2">
                <a
                  [routerLink]="['/reports', 'dynamic', 'new']"
                  [queryParams]="laborTrackingReportParams$ | async"
                  target="_blank"
                  >See crew pay rows</a
                >. That report shows the <em>saved</em> crew pay values, so it can differ from
                this table until you Save (which recalculates each changed job).
              </div>
            </app-dashboard-info-v2>
          </div>
        </div>
        <p-splitButton
          label="Copy Data"
          icon="pi pi-copy"
          (onClick)="copyToClipboard()"
          [model]="exportActions"
          [disabled]="exportDisabled"
          [pTooltip]="exportDisabledReason"
          tooltipPosition="left"
          appendTo="body"
        ></p-splitButton>
      </div>

      <ng-container *ngIf="!loading">
        <!-- #6538: the response could not be trusted, so no rows are shown. This replaces the empty
             state rather than joining it — "no jobs in this period" would be read as "nobody to pay"
             and is not what happened. Refreshing is a complete recovery: nothing was saved.
             Also shown for a plain `error`: a request that fails outright never sets `stale`, so
             that case previously reported itself only through the small icon beside the title —
             easy to miss next to a confident (and wrong) empty state. -->
        <p-messages
          *ngIf="stale || error"
          [value]="[{ severity: 'error', summary: 'Crew pay could not be loaded', detail: error, closable: false }]"
          [enableService]="false"
        ></p-messages>

        <app-dashboard-empty-state
          *ngIf="isEmpty"
          text="No booked or invoiced OTR jobs with crew pay in the selected period"
        ></app-dashboard-empty-state>

        <ng-container *ngFor="let group of periodGroups">
          <div class="period-group">
            <div class="period-group__header" (click)="togglePeriod(group.periodStart)">
              <i class="pi" [ngClass]="isPeriodExpanded(group.periodStart) ? 'pi-chevron-down' : 'pi-chevron-right'"></i>
              <span class="period-label">{{ group.label }}</span>
              <ng-container *ngIf="group.payoutDate">
                <span class="period-separator">|</span>
                <span class="period-payout">Payout Date: {{ group.payoutDate }}</span>
              </ng-container>
              <span class="period-count">{{ group.jobCount }} job{{ group.jobCount === 1 ? '' : 's' }}<ng-container *ngIf="unpaidJobCount(group) as unpaidJobs"> / {{ unpaidJobs }} unpaid job{{ unpaidJobs === 1 ? '' : 's' }}</ng-container><ng-container *ngIf="noCrewJobCount(group) as noCrewJobs"> / {{ noCrewJobs }} job{{ noCrewJobs === 1 ? '' : 's' }} without crew</ng-container></span>
              <button
                *ngIf="group.payoutDate"
                type="button"
                class="pay-all-btn"
                [disabled]="!groupHasUnpaid(group)"
                (click)="$event.stopPropagation(); payAllInGroup(group)"
                pTooltip="Set Paid to Total for every crew member in this period"
                tooltipPosition="top"
              >Pay all jobs in this group</button>
              <!-- stopPropagation: this header row is the accordion toggle. -->
              <button
                type="button"
                class="export-btn"
                [disabled]="exportDisabled"
                (click)="$event.stopPropagation(); copyGroupToClipboard(group)"
                [pTooltip]="exportDisabled ? exportDisabledReason : 'Copy only this payment period to the clipboard'"
                tooltipPosition="top"
              ><i class="pi pi-copy"></i> Copy</button>
            </div>

            <div *ngIf="isPeriodExpanded(group.periodStart)" class="period-group__content">
              <div class="table-scroll">
                <table class="labor-table" [class.labor-table-dark]="isDark">
              <thead>
                <tr>
                  <th>Job</th>
                  <!-- #6751: the franchise(s) this job's events were shared into. Same column and
                       same header as the OTR - System Revenue drill-down. -->
                  <th class="col-franchises">Shared With Franchises</th>
                  <th>Customer</th>
                  <th>Last Event Date</th>
                  <th>Creation Date</th>
                  <th>Crew Member</th>
                  <!-- #6620: who ran the move. A Third-Party job funds no FP labor, so its crew
                       amounts are shown but its crew-size expectations do not apply. -->
                  <th class="col-exec-type">Execution</th>
                  <!-- #6538: the FP pays this member locally — zero their crew pay and forward it. -->
                  <th class="col-check col-pay-to-fp">Pay Labor to FP</th>
                  <th class="col-check">Crew Lead</th>
                  <th class="col-check">Reg. Crew</th>
                  <th class="col-check">Packing</th>
                  <th class="col-check">Unpacking</th>
                  <th class="col-check">Load</th>
                  <th class="col-check">Unload</th>
                  <th class="col-check">Bonus</th>
                  <th class="col-amount">Total fees for services</th>
                  <th class="col-amount">Tips</th>
                  <th class="col-amount">Total</th>
                  <th class="col-amount">Paid</th>
                  <th class="col-amount">Unpaid</th>
                  <th class="col-check col-doc col-doc-start">Contract For This Job</th>
                  <th class="col-check col-doc">ACH Info</th>
                  <th class="col-check col-doc">W-9</th>
                </tr>
              </thead>
              <tbody>
                <!-- #6751: row-job-alt zebra-stripes by job, not by row — every row of one job
                     shares the tint. row-unpaid still wins (see the SCSS). -->
                <tr
                  *ngFor="let row of group.rows"
                  [class.row-job-alt]="row.jobStripe"
                  [class.row-unpaid]="showUnpaidWarning(group, row)"
                >
                  <td class="col-code">
                    <a *ngIf="row.jobUrl" [href]="row.jobUrl" target="_blank" class="job-link">{{ row.jobCode }}</a>
                    <span *ngIf="!row.jobUrl">{{ row.jobCode }}</span>
                    <i
                      *ngIf="row.warnings.length"
                      class="pi pi-exclamation-triangle row-warning"
                      [pTooltip]="row.warnings.join(' • ')"
                      tooltipPosition="top"
                    ></i>
                  </td>
                  <!-- #6751: blank when the job was never shared. A job shared into several
                       franchises is cut off to keep the row height fixed, with the full list on hover.
                       #6990: one colour-coded chip per franchise (franchise-chip-colors.ts). -->
                  <td class="col-franchises">
                    <span
                      class="franchises-value"
                      [pTooltip]="row.sharedFranchises"
                      tooltipPosition="top"
                    >
                      <span
                        *ngFor="let chip of row.sharedFranchiseChips"
                        class="franchise-chip"
                        [ngClass]="chip.colorClass"
                      >{{ chip.name }}</span>
                    </span>
                  </td>
                  <!-- #6990: names bolded; the "No crew assigned" placeholder stays plain. -->
                  <td class="cell-name">{{ row.customerName }}</td>
                  <td class="col-date">{{ row.lastEventDate }}</td>
                  <td class="col-date">{{ row.creationDate }}</td>
                  <td [class.col-na]="row.noCrew" [class.cell-name]="!row.noCrew">{{ row.crewMemberName }}</td>

                  <!-- #6620: only flagged when it is not the ordinary case, so the column stays quiet
                       on the Partner FP jobs that make up almost all of the view. -->
                  <td class="col-exec-type">
                    <span
                      *ngIf="row.jobExecutionType !== 'partnerFp'"
                      class="exec-type-tag"
                      [class.exec-type-third-party]="row.isThirdParty"
                    >{{ row.jobExecutionLabel }}</span>
                  </td>

                  <td class="col-check col-pay-to-fp">
                    <span *ngIf="row.noCrew" class="col-na">—</span>
                    <p-checkbox
                      *ngIf="!row.noCrew"
                      [(ngModel)]="row.payToFp"
                      [binary]="true"
                      [disabled]="payToFpLocked(row)"
                      (onChange)="togglePayToFp(row, $event.checked)"
                      [pTooltip]="payToFpTooltip(row)"
                      tooltipPosition="top"
                    ></p-checkbox>
                  </td>

                  <td class="col-check">
                    <span *ngIf="payLocked(row)" class="col-na">—</span>
                    <ng-container *ngIf="!payLocked(row)">
                      <p-checkbox
                        [(ngModel)]="row.crewLeadEnabled"
                        [binary]="true"
                        [disabled]="payReadOnly(row)"
                        (onChange)="toggleComponent(row, 'crewLead', 'crewLeadEnabled', $event.checked)"
                      ></p-checkbox>
                      <span class="rate-hint" *ngIf="row.crewLeadEnabled && !payReadOnly(row)">{{ row.crewLeadRate | currency:'USD':'symbol':'1.2-2' }}</span>
                    </ng-container>
                  </td>

                  <td class="col-check">
                    <span *ngIf="row.flatRateDisabled || payLocked(row)" class="col-na">—</span>
                    <ng-container *ngIf="!row.flatRateDisabled && !payLocked(row)">
                      <p-checkbox
                        [(ngModel)]="row.flatRateEnabled"
                        [binary]="true"
                        [disabled]="payReadOnly(row)"
                        (onChange)="toggleComponent(row, 'flatRate', 'flatRateEnabled', $event.checked)"
                      ></p-checkbox>
                      <span class="rate-hint" *ngIf="row.flatRateEnabled && !payReadOnly(row)">{{ row.flatRateRate | currency:'USD':'symbol':'1.2-2' }}</span>
                    </ng-container>
                  </td>

                  <td class="col-check">
                    <span *ngIf="row.packingDisabled || payLocked(row)" class="col-na">—</span>
                    <ng-container *ngIf="!row.packingDisabled && !payLocked(row)">
                      <p-checkbox
                        [(ngModel)]="row.packingEnabled"
                        [binary]="true"
                        [disabled]="payReadOnly(row)"
                        (onChange)="toggleComponent(row, 'packing', 'packingEnabled', $event.checked)"
                      ></p-checkbox>
                      <span class="rate-hint" *ngIf="row.packingEnabled && !payReadOnly(row)">{{ row.packingRate | currency:'USD':'symbol':'1.2-2' }}</span>
                    </ng-container>
                  </td>

                  <td class="col-check">
                    <span *ngIf="row.unpackingDisabled || payLocked(row)" class="col-na">—</span>
                    <ng-container *ngIf="!row.unpackingDisabled && !payLocked(row)">
                      <p-checkbox
                        [(ngModel)]="row.unpackingEnabled"
                        [binary]="true"
                        [disabled]="payReadOnly(row)"
                        (onChange)="toggleComponent(row, 'unpacking', 'unpackingEnabled', $event.checked)"
                      ></p-checkbox>
                      <span class="rate-hint" *ngIf="row.unpackingEnabled && !payReadOnly(row)">{{ row.unpackingRate | currency:'USD':'symbol':'1.2-2' }}</span>
                    </ng-container>
                  </td>

                  <td class="col-check">
                    <span *ngIf="row.loadDisabled || payLocked(row)" class="col-na">—</span>
                    <ng-container *ngIf="!row.loadDisabled && !payLocked(row)">
                      <p-checkbox
                        [(ngModel)]="row.loadEnabled"
                        [binary]="true"
                        [disabled]="payReadOnly(row)"
                        (onChange)="toggleComponent(row, 'load', 'loadEnabled', $event.checked)"
                      ></p-checkbox>
                      <span class="rate-hint" *ngIf="row.loadEnabled && !payReadOnly(row)">{{ effectiveLoadRate(row) | currency:'USD':'symbol':'1.2-2' }}</span>
                    </ng-container>
                  </td>

                  <td class="col-check">
                    <span *ngIf="row.unloadDisabled || payLocked(row)" class="col-na">—</span>
                    <ng-container *ngIf="!row.unloadDisabled && !payLocked(row)">
                      <p-checkbox
                        [(ngModel)]="row.unloadEnabled"
                        [binary]="true"
                        [disabled]="payReadOnly(row)"
                        (onChange)="toggleComponent(row, 'unload', 'unloadEnabled', $event.checked)"
                      ></p-checkbox>
                      <span class="rate-hint" *ngIf="row.unloadEnabled && !payReadOnly(row)">{{ effectiveUnloadRate(row) | currency:'USD':'symbol':'1.2-2' }}</span>
                    </ng-container>
                  </td>

                  <td class="col-check">
                    <span *ngIf="row.bonusDisabled || payLocked(row)" class="col-na">—</span>
                    <ng-container *ngIf="!row.bonusDisabled && !payLocked(row)">
                      <p-checkbox
                        [(ngModel)]="row.bonusEnabled"
                        [binary]="true"
                        [disabled]="payReadOnly(row)"
                        (onChange)="toggleComponent(row, 'bonus', 'bonusEnabled', $event.checked)"
                      ></p-checkbox>
                      <span class="rate-hint" *ngIf="row.bonusEnabled && !payReadOnly(row)">{{ row.bonusRate | currency:'USD':'symbol':'1.2-2' }}</span>
                    </ng-container>
                  </td>

                  <!-- #6892: an overridden fee shows BOTH figures. The amount owed is the manual
                       override; the phase columns keep their own total underneath it, because on an
                       overridden row the checkboxes no longer move the fee (the calculator ignores
                       their sum while an override is set). Without the second figure that reads as
                       a broken checkbox rather than as a deliberate override, and whoever is
                       authorising the payment cannot see what it was overridden away from. -->
                  <td class="col-amount">
                    <span *ngIf="row.noCrew" class="col-na">—</span>
                    <ng-container *ngIf="!row.noCrew">
                      <span>{{ row.fees | currency:'USD':'symbol':'1.2-2' }}</span>
                      <span
                        *ngIf="showFeesOverride(row)"
                        class="fees-calc-hint"
                        [pTooltip]="feesOverrideTooltip"
                        tooltipPosition="top"
                      >calc {{ row.feesCalculated | currency:'USD':'symbol':'1.2-2' }}</span>
                    </ng-container>
                  </td>

                  <td class="col-amount">
                    <span *ngIf="row.noCrew" class="col-na">—</span>
                    <span *ngIf="!row.noCrew">{{ row.tip | currency:'USD':'symbol':'1.2-2' }}</span>
                  </td>

                  <td class="col-amount col-total">
                    <span *ngIf="row.noCrew" class="col-na">—</span>
                    <span *ngIf="!row.noCrew">{{ row.total | currency:'USD':'symbol':'1.2-2' }}</span>
                  </td>

                  <td class="col-amount">
                    <span *ngIf="row.noCrew" class="col-na">—</span>
                    <div *ngIf="!row.noCrew" class="paid-cell">
                      <button
                        type="button"
                        class="paid-fill-btn"
                        [disabled]="!hasUnpaidBalance(row)"
                        pTooltip="Set Paid to Total"
                        tooltipPosition="top"
                        aria-label="Set Paid to Total"
                        (click)="markPaidInFull(row)"
                      >Pay</button>
                      <!-- #6538: a forwarded member's labor goes to the FP but their TIP does not,
                           so they can still be owed money here and their payment still needs
                           recording. `hasUnpaidBalance` alone gates the Pay button. -->
                      <p-inputNumber
                        class="amount-input"
                        [(ngModel)]="row.paid"
                        (onBlur)="commitPaid(row)"
                        mode="currency"
                        currency="USD"
                        locale="en-US"
                        [min]="0"
                        [minFractionDigits]="2"
                        [maxFractionDigits]="2"
                      ></p-inputNumber>
                    </div>
                  </td>

                  <td class="col-amount col-unpaid" [class.unpaid-positive]="showUnpaidWarning(group, row)">
                    <span *ngIf="row.noCrew" class="col-na">—</span>
                    <span *ngIf="!row.noCrew">{{ row.unpaid | currency:'USD':'symbol':'1.2-2' }}</span>
                  </td>

                  <!-- Contract For This Job (per job × crew member): staged, saved on Save. -->
                  <td class="col-check col-doc col-doc-start">
                    <span *ngIf="row.noCrew" class="col-na">—</span>
                    <p-checkbox
                      *ngIf="!row.noCrew"
                      [(ngModel)]="row.contractForThisJob"
                      [binary]="true"
                      (onChange)="toggleContract(row, $event.checked)"
                    ></p-checkbox>
                  </td>

                  <!-- ACH Info / W-9 (per crew member, global): saved immediately on toggle,
                       with an inline saving spinner while the write is in flight. -->
                  <td class="col-check col-doc">
                    <span *ngIf="row.noCrew" class="col-na">—</span>
                    <ng-container *ngIf="!row.noCrew">
                      <p-checkbox
                        [(ngModel)]="row.achInfo"
                        [binary]="true"
                        [disabled]="isDocFlagSaving(row, 'ach')"
                        (onChange)="toggleUserDocFlag(row, 'ach', $event.checked)"
                      ></p-checkbox>
                      <i *ngIf="isDocFlagSaving(row, 'ach')" class="pi pi-spin pi-spinner doc-flag-spinner"></i>
                    </ng-container>
                  </td>

                  <td class="col-check col-doc">
                    <span *ngIf="row.noCrew" class="col-na">—</span>
                    <ng-container *ngIf="!row.noCrew">
                      <p-checkbox
                        [(ngModel)]="row.w9"
                        [binary]="true"
                        [disabled]="isDocFlagSaving(row, 'w9')"
                        (onChange)="toggleUserDocFlag(row, 'w9', $event.checked)"
                      ></p-checkbox>
                      <i *ngIf="isDocFlagSaving(row, 'w9')" class="pi pi-spin pi-spinner doc-flag-spinner"></i>
                    </ng-container>
                  </td>
                </tr>
              </tbody>
            </table>
              </div>
            </div>
          </div>
        </ng-container>
      </ng-container>
    </div>
  </div>

  <!-- Sticky unsaved-changes banner (mirrors the Job page's app-unsaved-changes-banner,
       but wired to this dashboard's own pendingByJob dirty state). Save commits; Discard
       backs out staged edits — giving a confirmation step for bulk "Pay all". -->
  <div
    *ngIf="hasUnsavedChanges || saving"
    class="sticky-unsaved-changes-banner"
  >
    <span class="banner-label">
      <i class="pi" [ngClass]="saving ? 'pi-spin pi-spinner' : 'pi-exclamation-triangle'"></i>
      Unsaved changes
    </span>
    <span class="buttons-container">
      <button
        type="button"
        class="banner-btn"
        [disabled]="saving"
        pTooltip="Discard unsaved changes"
        tooltipPosition="top"
        (click)="discardChanges()"
      >Discard</button>
      <!-- #6538: Save is blocked while the last response was unusable — the rows it applies to are
           no longer on screen, so it would commit edits nobody can check. Discard stays enabled so
           staged work can still be backed out. -->
      <button
        type="button"
        class="banner-btn"
        [disabled]="saving || stale"
        [pTooltip]="stale ? 'Refresh the dashboard before saving — the current figures could not be loaded' : ''"
        tooltipPosition="top"
        (click)="saveChanges()"
      >{{ saving ? 'Saving…' : 'Save' }}</button>
    </span>
  </div>
</app-dashboard-shell>
